/**
 * HexColorPicker - Componente reutilizável de seleção de cor com suporte a código hex
 * 
 * Exibe um preview colorido clicável (abre color picker nativo) + campo texto para hex.
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-06 v1.0.0 - Criação do componente
 */

import { useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface HexColorPickerProps {
  /** Valor atual da cor em formato hex (ex: #3b82f6) */
  value: string;
  /** Callback chamado quando a cor é alterada */
  onChange: (color: string) => void;
  /** Label exibido acima do campo */
  label?: string;
}

/**
 * Valida se uma string é um código hex válido
 */
function isValidHex(hex: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(hex);
}

/**
 * Seletor de cor com preview + campo hex editável
 * - Clicar no quadrado colorido abre o color picker nativo do navegador
 * - Campo texto permite digitar código hex diretamente
 */
export function HexColorPicker({ value, onChange, label = 'Cor' }: HexColorPickerProps) {
  const colorInputRef = useRef<HTMLInputElement>(null);

  /** Abre o color picker nativo ao clicar no preview */
  const handlePreviewClick = () => {
    colorInputRef.current?.click();
  };

  /** Atualiza cor via color picker nativo */
  const handleNativeColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  /** Atualiza cor via campo texto hex */
  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let hex = e.target.value;

    // Adiciona # automaticamente se o usuário não digitou
    if (hex && !hex.startsWith('#')) {
      hex = `#${hex}`;
    }

    // Atualiza sempre o valor para permitir digitação livre
    // Mas só aplica a cor se for hex válido
    if (isValidHex(hex)) {
      onChange(hex);
    } else {
      // Permite digitação parcial sem travar o campo
      onChange(hex);
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        {/* Preview colorido clicável */}
        <button
          type="button"
          onClick={handlePreviewClick}
          className="h-10 w-10 rounded-md border border-input shrink-0 cursor-pointer transition-all hover:scale-105"
          style={{ backgroundColor: isValidHex(value) ? value : '#cccccc' }}
          title="Clique para abrir o seletor de cores"
        />

        {/* Input nativo hidden */}
        <input
          ref={colorInputRef}
          type="color"
          value={isValidHex(value) ? value : '#3b82f6'}
          onChange={handleNativeColorChange}
          className="sr-only"
          tabIndex={-1}
        />

        {/* Campo texto para código hex */}
        <Input
          type="text"
          value={value}
          onChange={handleHexInputChange}
          placeholder="#3b82f6"
          maxLength={7}
          className="font-mono uppercase"
        />
      </div>
    </div>
  );
}
